/**
 * Architecture Decision Records (ADR) Utilities
 */

import { ADR, ADRStatus, ExtendedArchiMateDiagram } from './extended-types';
import { uuidv7 } from 'uuidv7';

export function createADR(
  title: string,
  context: string,
  decision: string,
  consequences: string,
  previousNumber: number = 0,
  relatedElements: string[] = []
): ADR {
  return {
    id: uuidv7(),
    number: previousNumber + 1,
    title,
    status: 'proposed',
    date: new Date().toISOString().split('T')[0],
    context,
    decision,
    consequences,
    relatedElements,
    tags: [],
    createdAt: Date.now(),
    updatedAt: Date.now(),
  };
}

export function updateADRStatus(adr: ADR, status: ADRStatus): ADR {
  return {
    ...adr,
    status,
    updatedAt: Date.now(),
  };
}

export function supersedeADR(oldADR: ADR, newADR: ADR): [ADR, ADR] {
  return [
    { ...oldADR, status: 'superseded', supersededBy: newADR.id, updatedAt: Date.now() },
    { ...newADR, supersedes: oldADR.id },
  ];
}

export function getADRsByElement(
  diagram: ExtendedArchiMateDiagram,
  elementId: string
): ADR[] {
  return diagram.adrs.filter((adr) => adr.relatedElements.includes(elementId));
}

export function getADRsByStatus(
  diagram: ExtendedArchiMateDiagram,
  status: ADRStatus
): ADR[] {
  return diagram.adrs.filter((adr) => adr.status === status);
}

export function getADRTimeline(diagram: ExtendedArchiMateDiagram): ADR[] {
  return [...diagram.adrs].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
}

export function generateADRMarkdown(adr: ADR): string {
  let md = `# ADR ${adr.number}: ${adr.title}\n\n`;
  md += `## Status\n\n${adr.status}\n\n`;
  md += `**Date:** ${adr.date}\n`;
  if (adr.author) md += `**Author:** ${adr.author}\n`;
  md += `\n## Context\n\n${adr.context}\n`;
  md += `\n## Decision\n\n${adr.decision}\n`;
  md += `\n## Consequences\n\n${adr.consequences}\n`;
  if (adr.alternatives) md += `\n## Alternatives Considered\n\n${adr.alternatives}\n`;
  if (adr.supersedes) md += `\n**Supersedes:** ADR ${adr.supersedes}\n`;
  if (adr.supersededBy) md += `\n**Superseded By:** ADR ${adr.supersededBy}\n`;
  return md;
}

export function exportADRsToMarkdown(diagram: ExtendedArchiMateDiagram): string {
  let content = `# Architecture Decision Records\n\n`;
  content += `## ${diagram.name}\n\n`;
  content += `Total ADRs: ${diagram.adrs.length}\n\n`;

  const timeline = getADRTimeline(diagram);
  timeline.forEach((adr) => {
    content += `\n---\n\n`;
    // Generate markdown and adjust heading levels for export (# becomes ##, ## becomes ###, etc.)
    const adrMarkdown = generateADRMarkdown(adr).replace(/^#/gm, '##');
    content += adrMarkdown;
  });

  return content;
}

export function searchADRs(diagram: ExtendedArchiMateDiagram, query: string): ADR[] {
  const lowerQuery = query.toLowerCase();
  return diagram.adrs.filter(
    (adr) =>
      adr.title.toLowerCase().includes(lowerQuery) ||
      adr.context.toLowerCase().includes(lowerQuery) ||
      adr.decision.toLowerCase().includes(lowerQuery) ||
      adr.tags.some((tag) => tag.toLowerCase().includes(lowerQuery))
  );
}

/**
 * Get ADR template based on format
 */
export function getADRTemplate(format: 'nygard' | 'madr' | 'y-statement' | 'blank'): {
  context: string;
  decision: string;
  consequences: string;
  alternatives?: string;
} {
  switch (format) {
    case 'nygard':
      return {
        context: 'Describe the forces at play, including technological, political, social, and project local.',
        decision: 'Describe our response to these forces.',
        consequences: 'Describe the resulting context, after applying the decision. All consequences should be listed here, not just the "positive" ones.',
      };
    case 'madr':
      return {
        context: 'Issue we are facing in this context and problem statement.',
        decision: 'Explain the chosen option and the reasons for choosing it.',
        consequences: 'Positive and negative outcomes of the decision.',
      };
    case 'y-statement':
      return {
        context: 'In the context of [use case/user story]',
        decision: 'facing concern, we decided for [option] to achieve [quality], accepting [downside].',
        consequences: '',
      };
    case 'blank':
      return {
        context: '',
        decision: '',
        consequences: '',
        alternatives: '',
      };
  }
}
